import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    Select,
    InputNumber,
    Row,
    Col,
    Typography,
    message,
    Spin,
    Popconfirm,
    Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined, FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import api, { imagepath } from '../api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

function PettyCashExport() {
    const [dates, setDates] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleExport = async () => {
        if (!dates || dates.length !== 2) {
            message.error('Please select a date range');
            return;
        }
        setLoading(true);
        try {
            const res = await api.post(
                '/petty-cash/export-pdf',
                {
                    date_from: dayjs(dates[0]).format('YYYY-MM-DD'),
                    date_to: dayjs(dates[1]).format('YYYY-MM-DD'),
                },
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'petty-cash-statement.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Export error:', err);
            message.error('Failed to export PDF');
        }
        setLoading(false);
    };

    return (
        <div style={{ display: 'flex', gap: 8 }}>
            <RangePicker onChange={setDates} style={{ width: 250 }} />
            <Button type="default" onClick={handleExport} loading={loading}>
                Export PDF
            </Button>
        </div>
    );
}

export default function PettyCash() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter states
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1); // 1-12

    // Summary states
    const [openingBalance, setOpeningBalance] = useState(0);
    const [monthIncome, setMonthIncome] = useState(0);
    const [monthExpense, setMonthExpense] = useState(0);

    // Month Closing State
    const [lastClosedMonth, setLastClosedMonth] = useState(null);
    const [isMonthClosed, setIsMonthClosed] = useState(false);
    const [canAdd, setCanAdd] = useState(true);

    // Modal
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        fetchStatus();
    }, []);

    useEffect(() => {
        // Fetch data whenever filters change
        fetchData();
        checkMonthStatus();
    }, [selectedYear, selectedMonth, lastClosedMonth]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const startOfMonth = dayjs(`${selectedYear}-${selectedMonth}-01`).startOf('month');
            const endOfMonth = startOfMonth.endOf('month');

            const res = await api.get('/petty-cash', {
                params: {
                    date_from: startOfMonth.format('YYYY-MM-DD'),
                    date_to: endOfMonth.format('YYYY-MM-DD')
                }
            });

            if (res.success) {
                setData(res.data); // Data is now ONLY for this month
                setOpeningBalance(Number(res.opening_balance || 0));

                // Calculate totals locally from the fetched chunk
                const inc = res.data.filter(d => d.type === 'income').reduce((sum, d) => sum + Number(d.amount), 0);
                const exp = res.data.filter(d => d.type === 'expense').reduce((sum, d) => sum + Number(d.amount), 0);
                setMonthIncome(inc);
                setMonthExpense(exp);

            } else {
                message.error(res.message || 'Failed to fetch petty cash data');
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to fetch data');
        }
        setLoading(false);
    };

    const fetchStatus = async () => {
        try {
            const res = await api.get('/petty-cash/status');
            if (res.success) {
                setLastClosedMonth(res.data.last_closed_month);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const checkMonthStatus = () => {
        if (!lastClosedMonth) {
            setIsMonthClosed(false);
            setCanAdd(true);
            return;
        }
        const currentKey = dayjs(`${selectedYear}-${selectedMonth}-01`).format('YYYY-MM');
        if (currentKey <= lastClosedMonth) {
            setIsMonthClosed(true);
            setCanAdd(false);
        } else {
            setIsMonthClosed(false);
            // Can only add to the immediate next month
            const nextOpen = dayjs(lastClosedMonth + '-01').add(1, 'month').format('YYYY-MM');
            if (currentKey === nextOpen) {
                setCanAdd(true);
            } else {
                setCanAdd(false);
            }
        }
    };

    // Closing balance = Opening + Income - Expense
    // OR the balance of the last transaction if exists.
    // Note: Since DB stores running balance, the specific running balance of last transaction is accurate.
    // BUT if we deleted transactions in middle, re-calc might be needed if DB isn't updated? 
    // Backend ensures integrity on store(). 
    // Let's rely on math: Open + Inc - Exp.
    const currentBalance = openingBalance + monthIncome - monthExpense;

    const columns = [
        { title: '#', key: 'index', render: (text, record, index) => index + 1, width: 50 },
        { title: 'Date', dataIndex: 'date', key: 'date', render: (t) => dayjs(t).format('DD-MM-YYYY') },
        { title: 'Description', dataIndex: 'description', key: 'description' },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (t) =>
                t === 'income' ? (
                    <span style={{ color: 'green' }}>Income</span>
                ) : (
                    <span style={{ color: 'red' }}>Expense</span>
                ),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (p) => p ? Number(p).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'
        },
        {
            title: 'VAT %',
            dataIndex: 'vat_percent',
            key: 'vat_percent',
            render: (v) => v ? `${v}%` : '0%'
        },
        {
            title: 'VAT No',
            dataIndex: 'vat_no',
            key: 'vat_no',
            render: (v) => v || '-'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (a) => <b>{Number(a).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>,
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            render: (b) => <span>{Number(b).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>,
        },
        {
            title: 'Attachment',
            dataIndex: 'attachment',
            key: 'attachment',
            render: (attachment) => {
                if (!attachment) return '-';
                const isPdf = attachment.toLowerCase().endsWith('.pdf');
                return (
                    <Button 
                        type="link" 
                        icon={isPdf ? <FilePdfOutlined style={{ color: '#cf1322' }} /> : <FileImageOutlined />} 
                        onClick={() => window.open(`${imagepath}${attachment}`, '_blank')}
                        title={isPdf ? "View PDF Document" : "View Bill Image"}
                    >
                        View
                    </Button>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => handleEdit(record)}
                        disabled={!canAdd}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this transaction?"
                        description="This will recalculate balances."
                        onConfirm={() => handleDelete(record.id)}
                        disabled={!canAdd}
                        okText="Yes, Delete"
                        cancelText="No"
                    >
                        <Button
                            type="link"
                            danger
                            size="small"
                            disabled={!canAdd}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </div>
            )
        }
    ];

    const handleAdd = () => {
        setEditingId(null);
        setFileList([]);
        form.resetFields();
        // Default date to today, default type to expense
        form.setFieldsValue({
            date: dayjs(),
            type: 'expense'
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        setFileList(record.attachment ? [{
            uid: '-1',
            name: record.attachment.split('/').pop(),
            status: 'done',
            url: `${imagepath}${record.attachment}`
        }] : []);
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(record.date),
            description: record.description,
            type: record.type,
            price: record.price,
            vat_percent: record.vat_percent || 0,
            vat_no: record.vat_no,
            amount: record.amount
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        if (!canAdd) {
            message.warning('Cannot delete in a closed month');
            return;
        }
        try {
            console.log('Deleting transaction:', id);
            const res = await api.delete(`/petty-cash/${id}`);
            if (res.success) {
                message.success('Transaction deleted');
                fetchData();
            } else {
                message.error(res.message || 'Failed to delete');
            }
        } catch (err) {
            console.error('Delete error:', err);
            message.error('Error deleting transaction');
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();
            formData.append('date', values.date.format('YYYY-MM-DD'));
            formData.append('description', values.description);
            formData.append('type', values.type);
            if (values.price !== undefined && values.price !== null) {
                formData.append('price', values.price);
            }
            if (values.vat_percent !== undefined && values.vat_percent !== null) {
                formData.append('vat_percent', values.vat_percent);
            }
            if (values.vat_no !== undefined && values.vat_no !== null) {
                formData.append('vat_no', values.vat_no);
            }
            formData.append('amount', values.amount);

            if (fileList.length > 0 && fileList[0].originFileObj) {
                formData.append('attachment', fileList[0].originFileObj);
            } else if (fileList.length === 0 && editingId) {
                // Explicitly request removal of attachment if it was cleared in edit mode
                formData.append('remove_attachment', 'true');
            }

            let res;
            if (editingId) {
                // Since Laravel handles PUT requests with files poorly, we use POST with _method=PUT
                res = await api.post(`/petty-cash/${editingId}?_method=PUT`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                // Create
                res = await api.post('/petty-cash', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            if (res.success) {
                message.success(editingId ? 'Transaction updated' : 'Transaction added');
                setModalVisible(false);
                setFileList([]);
                fetchData();
            } else {
                message.error(res.message || 'Failed to add transaction');
            }
        } catch (err) {
            // Error handling
        }
    };

    return (
        <div style={{ padding: 12 }}>
            {/* Filters & Actions */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                    <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 100 }}>
                        {Array.from({ length: 10 }, (_, i) => dayjs().year() - 5 + i).map(y => (
                            <Option key={y} value={y}>{y}</Option>
                        ))}
                    </Select>
                    <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 120 }}>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <Option key={m} value={m}>{dayjs().month(m - 1).format('MMMM')}</Option>
                        ))}
                    </Select>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <PettyCashExport />
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                            <Card size="small" bordered style={{ background: '#f0f5ff' }}>
                                <b>Opening Balance</b>
                                <div style={{ fontSize: 22, color: '#1890ff' }}>
                                    AED {openingBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small" bordered style={{ background: '#f6ffed' }}>
                                <b>Total Income</b>
                                <div style={{ fontSize: 22, color: 'green' }}>
                                    AED {monthIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small" bordered style={{ background: '#fff1f0' }}>
                                <b>Total Expense</b>
                                <div style={{ fontSize: 22, color: 'red' }}>
                                    AED {monthExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small" bordered style={{ background: '#e6f7ff' }}>
                                <b>Current Balance</b>
                                <div style={{ fontSize: 22, color: '#1890ff' }}>
                                    AED {currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        style={{ marginBottom: 12 }}
                        disabled={!canAdd}
                    >
                        Add Transaction
                    </Button>
                    <div style={{ padding: 24, maxHeight: `calc(100vh - 330px)`, overflow: 'auto' }}>
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={data}
                            rowKey="id"
                            pagination={false} // Disable pagination
                            bordered
                        />
                    </div>
                </>
            )}

            <Modal
                title={editingId ? "Edit Transaction" : "Add Petty Cash Transaction"}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                okText="Save"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={(changedValues, allValues) => {
                        if (changedValues.price !== undefined || changedValues.vat_percent !== undefined) {
                            const price = Number(allValues.price || 0);
                            const vatPercent = Number(allValues.vat_percent || 0);
                            const calculatedAmount = price + (price * vatPercent / 100);
                            form.setFieldsValue({ amount: calculatedAmount });
                        }
                    }}
                >
                    <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                        <DatePicker
                            style={{ width: '100%' }}
                            disabledDate={(current) => {
                                // Disable dates not in current or previous month
                                const startOfPrevMonth = dayjs().subtract(1, 'month').startOf('month');
                                const endOfCurrentMonth = dayjs().endOf('month');
                                return current && (current < startOfPrevMonth || current > endOfCurrentMonth);
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="income">Income</Option>
                            <Option value="expense">Expense</Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item name="price" label="Price (Excl. VAT)">
                                <InputNumber style={{ width: '100%' }} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="vat_percent" label="VAT %">
                                <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="vat_no" label="VAT No">
                                <Input style={{ width: '100%' }} placeholder="TRN/VAT Code" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="amount"
                        label="Total Amount"
                        rules={[
                            { required: true, message: 'Please enter an amount' },
                            {
                                validator: (_, value) =>
                                    value && value > 0
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('Amount must be greater than zero')),
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            style={{ width: '100%' }}
                            prefix="AED"
                            formatter={(value) =>
                                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={(value) => value.replace(/AED\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item label="Attachment (Optional)">
                        <Upload
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={({ fileList }) => setFileList(fileList.slice(-1))}
                            maxCount={1}
                            accept="image/*,application/pdf"
                        >
                            <Button icon={<UploadOutlined />}>Select Attachment (Image/PDF)</Button>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
